import { TrendingDownIcon, TrendingUpIcon } from "lucide-react";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "@/utils/axiosInstance";
import { subMonths } from "date-fns";

const DashboardCards = () => {
  const [cards, setCards] = useState<any>();
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const fetchDashboardReports = async () => {
    try {
      const response = await axiosInstance.get(`/reports/dashboard`, {
        params: {
          pagination: JSON.stringify({ page: 1, perPage: 10 }),
          filter: JSON.stringify({}),
        },
      });

      setCards(response.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let isActive = true;

    const poll = async () => {
      await fetchDashboardReports();
      if (isActive) {
        setTimeout(poll, 5000);
      }
    };

    poll();
    return () => {
      isActive = false;
    };
  }, []);

  const renderCardContent = (
    title: string,
    value: string | number,
    trend: string,
    icon: JSX.Element,
    footerTitle: string,
    footerDesc: string,
    isNegative = false,
    onClick?: () => void,
  ) => (
    <Card
      className={`@container/card transition-colors ${onClick ? "cursor-pointer hover:bg-muted" : ""}`}
      onClick={onClick}
      data-slot="card"
    >
      <CardHeader className="relative">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums">
          {isLoading ? <Skeleton className="h-8 w-24" /> : value}
        </CardTitle>
      </CardHeader>
      <CardFooter className="flex-col items-start gap-1 text-sm">
        {isLoading ? (
          <>
            <Skeleton className="h-4 w-32 mb-1" />
            <Skeleton className="h-3 w-48" />
          </>
        ) : (
          <>
            <div className="text-muted-foreground">{footerDesc}</div>
          </>
        )}
      </CardFooter>
    </Card>
  );

  return (
    <div className="*:data-[slot=card]:shadow-xs @xl/main:grid-cols-2 @5xl/main:grid-cols-6 grid grid-cols-1 gap-4 px-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card lg:px-6">
      {renderCardContent(
        "Last 30 days",
        `${Number(Math.floor(cards?.totalFare) || 0).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} €`,
        "",
        <TrendingUpIcon className="size-3" />,
        "Revenue growth remains stable",
        "Your total earnings for the last 30 days",
        false,
        () =>
          navigate("/reports", {
            state: { startDate: subMonths(new Date(), 1) },
          }),
      )}

      {renderCardContent(
        "Orders",
        cards?.totalOrders,
        "",
        <TrendingUpIcon className="size-3" />,
        "Orders increased steadily",
        "Total number of orders placed for the last 30 days",
        false,
        () =>
          navigate("/orders", {
            state: {
              selectedDate: subMonths(new Date(), 1),
              endDate: new Date(),
            },
          }),
      )}

      {renderCardContent(
        "Commission Partners",
        cards?.totalCommission,
        "",
        <TrendingUpIcon className="size-3" />,
        "Partners driving more conversions",
        "Total partners on the platform",
        false,
        () => navigate("/referral-agents"),
      )}

      {renderCardContent(
        "Tours",
        cards?.totalTours,
        "",
        <TrendingDownIcon className="size-3" />,
        "Tour activity dropped slightly",
        "Total tours on the platform",
        false,
        () => navigate("/tours"),
      )}

      {renderCardContent(
        "Drivers",
        cards?.totalDrivers,
        "",
        <TrendingUpIcon className="size-3" />,
        "Driver engagement is high",
        "Total active drivers on the platform",
        false,
        () => navigate("/drivers"),
      )}

      {renderCardContent(
        "Vehicles",
        cards?.totalVehicles,
        "",
        <TrendingUpIcon className="size-3" />,
        "Fleet size expanded modestly",
        "Number of registered vehicles available",
        false,
        () => navigate("/vehicles"),
      )}
    </div>
  );
};

export default DashboardCards;
